import { encodePath, joinURL } from 'ufo';

import { createOperationsGenerator } from '#image';
import type { ProviderGetImage } from '@nuxt/image';

/**
 * Modified from the original:
 * https://raw.githubusercontent.com/nuxt/image/9be356e3f3cefe91d00641b303da5f3dc447524f/src/runtime/providers/sanity.ts
 */

const operationsGenerator = createOperationsGenerator({
  keyMap: {
    format: 'fm',
    height: 'h',
    quality: 'q',
    width: 'w',
    // Convenience modifiers
    background: 'bg',
    download: 'dl',
    dpr: 'dpr',
    sharpen: 'sharp',
    orientation: 'or',
    'min-height': 'min-h',
    'max-height': 'max-h',
    'min-width': 'min-w',
    'max-width': 'max-w',
    minHeight: 'min-h',
    maxHeight: 'max-h',
    minWidth: 'min-w',
    maxWidth: 'max-w',
    saturation: 'sat',
  },
  valueMap: {
    format: {
      jpeg: 'jpg',
    },
    fit: {
      cover: 'crop',
      contain: 'fill',
      fill: 'scale',
      inside: 'min',
      outside: 'max',
    },
  },
  formatter: (key, value: string | true | number | Record<string, unknown>) =>
    String(value) === 'true' ? key : encodePath(`${key}=${value}`),
});

const getMetadata = (id: string) => {
  const result = id.match(/-(?<width>\d*)x(?<height>\d*)-(?<format>.*)$/);
  if (!result || !result.groups) {
    // Invalid Sanity image asset ID
    if (import.meta.dev) {
      console.warn(`An invalid image asset ID was passed in: ${id}`);
    }
    return { width: undefined, height: undefined, format: undefined };
  }

  const width = Number(result.groups.width);
  const height = Number(result.groups.height);

  return {
    width,
    height,
    format: result.groups.format,
  };
};

export const getImage: ProviderGetImage = (src, { modifiers }) => {
  if (!modifiers) {
    console.warn(`No modifiers provided for image: ${src}`);
    modifiers = {};
  }

  // default to max
  if (!modifiers.fit) {
    modifiers.fit = 'outside';
  }

  const { height: sourceHeight, width: sourceWidth } = getMetadata(src);
  if (modifiers.crop && typeof modifiers.crop !== 'string' && sourceWidth && sourceHeight) {
    const left = modifiers.crop.left * sourceWidth;
    const top = modifiers.crop.top * sourceHeight;
    const right = sourceWidth - modifiers.crop.right * sourceWidth;
    const bottom = sourceHeight - modifiers.crop.bottom * sourceHeight;
    modifiers.rect = [left, top, right - left, bottom - top]
      .map((i) => i.toFixed(0))
      .join(',') as `${number},${number},${number},${number}`;
    delete modifiers.crop;
  }
  if (modifiers.hotspot && typeof modifiers.hotspot !== 'string') {
    modifiers['fp-x'] = modifiers.hotspot.x;
    modifiers['fp-y'] = modifiers.hotspot.y;
    delete modifiers.hotspot;
  }
  if (!modifiers.format || modifiers.format === 'auto') {
    if (modifiers.format === 'auto') {
      delete modifiers.format;
    }
    modifiers.auto = 'format';
  }
  if (modifiers.fit === 'contain' && !modifiers.bg) {
    modifiers.bg = 'ffffff';
  }
  const operations = operationsGenerator(modifiers);

  const parts = src.split('-').slice(1);
  const format = parts.pop();

  let operationsBits = operations.split('/') || [];

  // always choose the smallest `w` value
  const globalMaxImageWidth = 1600;
  const foundW = operationsBits
    .filter((bit) => bit.startsWith('w='))
    .map((bit) => parseInt(bit.split('=')[1], 10))
    .map((w) => (w > globalMaxImageWidth ? globalMaxImageWidth : w))
    .sort((lhs, rhs) => {
      return lhs - rhs;
    });

  if (foundW.length > 0) {
    operationsBits = operationsBits.filter((bit) => !bit.startsWith('w='));
    const w = foundW[0];
    operationsBits.unshift(`w=${w}`);
  }

  // svg images must not be changed to a different format
  if (format === 'svg') {
    operationsBits = operationsBits.filter((bit) => !bit.startsWith('fm='));
  }

  // not sure why it was needed to do the `split('/').join('&')` here
  const filenameAndQueries =
    parts.join('-') + '.' + format + (operations ? '?' + operationsBits.join('&') : '');

  return {
    url: joinURL('/', 'cdn-images', filenameAndQueries),
  };
};
